import React from "react";
import dynamic from "next/dynamic";

import { Text } from "@puzzle/ve";
import { vars } from "@puzzle/theme";
import { Paperclip } from "@puzzle/icons";

import { SidebarToggle } from "components/common/SidebarToggle";
import {
  SingleLedgerReconciliationFragment,
  StatementFragment,
} from "../LedgerReconciliation/graphql.generated";
import {
  uploadContent,
  attachmentRoot,
  sidebarToggle,
} from "components/common/AttachmentSidebar/AttachmentSidebar.css";
import { attachmentSidebarText } from "components/common/AttachmentSidebar/constants";
import { useAttachmentSidebar } from "components/common/AttachmentSidebar/useAttachmentSidebar";
import { useActiveCompany } from "components/companies/ActiveCompanyProvider";
import { SupportedUploadEntity } from "components/common/hooks/useAttachmentManager/useAttachmentManager";
import { DATA_TEST_IDS } from "../constants";
import { StatementsDropdownButton } from "./StatementsDropdownButton";
import {
  useLinkStoredFileToEntityMutation,
} from "components/common/files/graphql.generated";
import { AssociatedEntity } from "graphql/types";
import { useReconViewerContext } from "../LedgerReconciliation/ReconContext";
import { reportError } from "lib/errors/errors";

const DynamicPDFViewer = dynamic(() =>
  import("components/common/PDFViewer").then((mod) => mod.PDFViewer)
);

const UPLOAD_ICON_SIZE = 21;

type ReconAttachmentSidebarProps = {
  entity?: SingleLedgerReconciliationFragment;
  uploadEntityType: SupportedUploadEntity;
  inMemoryFile?: File;
  setInMemoryFile: (file?: File) => void;
  sidebarOpen: boolean;
  toggleSidebar?: () => void;
  isEditor: boolean;
  text?: string;
};

// TODO: Add dropdown menu for statement selection
export const ReconAttachmentSidebar = ({
  entity,
  inMemoryFile,
  setInMemoryFile,
  isEditor,
  toggleSidebar,
  sidebarOpen,
  uploadEntityType,
  text = attachmentSidebarText.defaultUploadText,
}: ReconAttachmentSidebarProps) => {
  const { company } = useActiveCompany<true>();
  const { statementFiles, setStatementFiles, activeLedgerAccount } = useReconViewerContext();
  const [linkStoredFileToEntity] = useLinkStoredFileToEntityMutation();
  const { handleFiles, dropAreaProps, over, previewFileUrl, isUploading, handleUnlinkFile } =
    useAttachmentSidebar({
      entity,
      uploadEntityType,
      inMemoryFile,
      setInMemoryFile,
      isEditor,
    });

  const accountId = activeLedgerAccount?.info?.financialInstitutionAccount?.id;
  const reconPreviewUrl = statementFiles.length > 0 ? statementFiles[0].url : previewFileUrl;
  const handleRemoveFile = () => {
    if (inMemoryFile) {
      setInMemoryFile(undefined);
      return;
    }
    if (!entity || !entity.storedFiles || entity.storedFiles.length === 0) return;

    handleUnlinkFile();
  };

  const handleSelectedStatement = (file: StatementFragment["file"]) => {
    if (entity) {
      linkStoredFileToEntity({
        variables: {
          input: {
            entityId: entity.id,
            entityType: AssociatedEntity.LedgerReconciliation,
            fileId: file?.id,
          },
        },
        onError: (err) => {
          reportError("Failed to link statement to reconciliation", { error: err });
        },
      });
    }

    if (file?.downloadInfo?.signedUrl) {
      setStatementFiles([
        {
          id: file.id,
          url: file.downloadInfo.signedUrl,
        },
      ]);
    }
  };

  return (
    <>
      {reconPreviewUrl && (
        <DynamicPDFViewer
          url={reconPreviewUrl}
          onToggle={toggleSidebar}
          onDelete={isEditor ? handleRemoveFile : undefined}
        />
      )}
      {!reconPreviewUrl && (
        <div
          {...dropAreaProps}
          className={attachmentRoot[over && isEditor ? "isDropping" : "default"]}
        >
          {toggleSidebar && (
            <SidebarToggle
              data-testid={DATA_TEST_IDS.SIDEBAR_TOGGLE}
              open={sidebarOpen}
              onClick={() => toggleSidebar()}
              className={sidebarToggle}
            />
          )}

          <div className={uploadContent}>
            <Paperclip size={UPLOAD_ICON_SIZE} color={vars.colors["text-neutral-base"]} />
            <Text variant="headingS" weight="bold" color="neutral-moreSubdue">
              {text}
            </Text>
            <Text color="neutral-subdue" variant="bodyS">
              {attachmentSidebarText.dragAndDropText}
            </Text>
            <StatementsDropdownButton
              accountId={accountId}
              companyId={company.id}
              handleUploadedFile={handleFiles}
              isUploading={isUploading}
              onSelectedStatement={handleSelectedStatement}
            />
          </div>
        </div>
      )}
    </>
  );
};
